import { useQuery } from '@apollo/client'

import GET_FIELDFOLIO_RECURRING_PLAN_DETAILS from './graphql/GetFieldfolioRecurringPlanDetails.graphql'
import {
  GetFieldfolioRecurringPlanDetails,
  GetFieldfolioRecurringPlanDetailsVariables
} from './graphql/__generated__/GetFieldfolioRecurringPlanDetails'
import { PlanEnum } from '../../__generated__/globalTypes'

const DEFAULT_PRICING_SET_ID = '2025-01-01'
const DEFAULT_TIER = 'BUSINESS'

type UseFieldfolioPricesProps = {
  pricingSetId?: string
}

type GetRecurringPlanPricingProps = {
  pricingSetId?: string
  tier?: 'BUSINESS'
  component?: 'base' | 'catalogs' | 'reps' | 'staff'
  currency?: 'AUD' | 'USD' | 'GBP'
}

type GetIncludedAddonsCountProps = {
  pricingSetId?: string
  tier?: 'BUSINESS'
  component: 'catalogs' | 'reps' | 'staff'
}

const useFieldfolioPrices = ({ pricingSetId }: UseFieldfolioPricesProps) => {
  const { loading, data } = useQuery<GetFieldfolioRecurringPlanDetails, GetFieldfolioRecurringPlanDetailsVariables>(
    GET_FIELDFOLIO_RECURRING_PLAN_DETAILS,
    {
      variables: { pricingSetId }
    }
  )

  const recurringTrialPeriodDays = data?.plans.find(plan => plan.id === PlanEnum.RECURRING)?.trialPeriodDays || 0

  const getRecurringPlanPricing = ({
    pricingSetId = DEFAULT_PRICING_SET_ID,
    tier = DEFAULT_TIER,
    component = 'base',
    currency = 'AUD'
  }: GetRecurringPlanPricingProps): number | null => {
    const recurringPlan = data?.plans.find(plan => plan.id === PlanEnum.RECURRING)
    if (!recurringPlan) return null

    const selectedPricingSet = recurringPlan.pricingSet?.find(ps => ps.id === pricingSetId)
    if (!selectedPricingSet) return null

    const selectedTier = selectedPricingSet.tiers?.find(t => t.id.toUpperCase() === tier.toUpperCase())
    if (!selectedTier) return null

    let price = 0

    switch (component) {
      case 'base':
        price = selectedTier.priceBase[currency.toLowerCase() as keyof typeof selectedTier.priceBase] as number
        break
      case 'catalogs':
        price = selectedTier.priceCatalogs[currency.toLowerCase() as keyof typeof selectedTier.priceCatalogs] as number
        break
      case 'reps':
        price = selectedTier.priceReps[currency.toLowerCase() as keyof typeof selectedTier.priceReps] as number
        break
      case 'staff':
        price = selectedTier.priceStaff[currency.toLowerCase() as keyof typeof selectedTier.priceStaff] as number
        break
    }

    return price
  }

  const getIncludedAddonsCount = ({
    pricingSetId = DEFAULT_PRICING_SET_ID,
    tier = DEFAULT_TIER,
    component = 'catalogs'
  }: GetIncludedAddonsCountProps): number => {
    const recurringPlan = data?.plans.find(plan => plan.id === PlanEnum.RECURRING)
    if (!recurringPlan) return 0

    const selectedPricingSet = recurringPlan.pricingSet?.find(ps => ps.id === pricingSetId)
    if (!selectedPricingSet) return 0

    const selectedTier = selectedPricingSet.tiers?.find(t => t.id.toUpperCase() === tier.toUpperCase())
    if (!selectedTier) return 0

    let count = 0

    switch (component) {
      case 'catalogs':
        count = selectedTier.includedCatalogs
        break
      case 'reps':
        count = selectedTier.includedReps
        break
      case 'staff':
        count = selectedTier.includedStaff
        break
    }

    return count
  }

  return {
    isFieldfolioRecurringPlanDetailsLoading: loading,
    fieldfolioRecurringPlanDetailsData: data,
    getRecurringPlanPricing,
    getIncludedAddonsCount,
    recurringTrialPeriodDays
  }
}

export default useFieldfolioPrices
